import {
  Badge,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useGetSiProductCategoryTreeApi, useGetSiProductProfileApi } from 'apis/supplier-intelligence';
import { ICategoryTreeItem } from 'pages/supplier-intelligence/utils/types';
import { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CategoryIntelligenceDrawer } from './category-intelligence-drawer';
import { CategoryTreeSection } from './category-tree-section';
import { CompetitorSuppliersSection } from './competitor-suppliers-section';
import { DemandMetricsSection } from './demand-metrics-section';
import { PriceBenchmarkSection } from './price-benchmark-section';

interface ProductIntelligenceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string | null;
  productIds?: number[];
  supplierId?: number;
  supplierName?: string;
}

export const ProductIntelligenceDrawer = ({
  isOpen,
  onClose,
  productName,
  productIds = [],
  supplierId,
  supplierName,
}: ProductIntelligenceDrawerProps) => {
  const history = useHistory();
  const match = useRouteMatch();
  const enabled = !!productName && isOpen;

  // Combined product profile (variants, competitors, price benchmark, demand)
  const { data: profile, isLoading } = useGetSiProductProfileApi(productIds, supplierId, { enabled });

  // Category tree (name-based)
  const { data: categoryTree } = useGetSiProductCategoryTreeApi(productName ?? '', { enabled });

  const { isOpen: isCategoryOpen, onOpen: onCategoryOpen, onClose: onCategoryClose } = useDisclosure();
  const [selectedCategoryPath, setSelectedCategoryPath] = useState<string[] | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategoryDepth, setSelectedCategoryDepth] = useState<number | null>(null);

  const handleCategoryClick = (items: ICategoryTreeItem[]) => {
    const clickedCategory = items[items.length - 1];
    setSelectedCategoryPath(items.map((c) => c.name));
    setSelectedCategoryId(clickedCategory.id);
    setSelectedCategoryDepth(clickedCategory.depth);
    onCategoryOpen();
  };

  if (!productName) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='xl'>
      <DrawerOverlay />
      <DrawerContent maxW='65vw'>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth='1px'>
          <Text fontSize='xl' fontWeight='bold'>
            {profile?.name || productName}
          </Text>
          <HStack spacing={4} mt={1} flexWrap='wrap'>
            {profile?.unit && (
              <Text fontSize='sm' color='gray.500'>
                Unit: {profile.unit}
              </Text>
            )}
            {profile?.category && (
              <Text fontSize='sm' color='gray.500'>
                Category: {profile.category}
              </Text>
            )}
            {profile?.brands && profile.brands.length > 0 && (
              <HStack spacing={1}>
                <Text fontSize='sm' color='gray.500'>
                  Brands:
                </Text>
                {profile.brands.map((b) => (
                  <Badge key={b} colorScheme='blue' fontSize='xs'>
                    {b}
                  </Badge>
                ))}
              </HStack>
            )}
            {supplierName && (
              <Text fontSize='sm' color='blue.500'>
                Via: {supplierName}
              </Text>
            )}
          </HStack>
        </DrawerHeader>

        <DrawerBody py={6}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {/* Variants Table */}
              {profile?.variants && profile.variants.length > 0 && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Variants ({profile.variants.length})
                    </Text>
                    <Box overflowX='auto'>
                      <Table size='sm' variant='simple'>
                        <Thead>
                          <Tr>
                            <Th>Product Name</Th>
                            <Th>Supplier</Th>
                            <Th>Brand</Th>
                            <Th>Unit</Th>
                            <Th isNumeric>Price (AED)</Th>
                            <Th>Specs</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {profile.variants.map((v) => (
                            <Tr key={v.productId}>
                              <Td fontWeight='medium'>{profile.name}</Td>
                              <Td>
                                {v.supplierId ? (
                                  <Text
                                    color='blue.600'
                                    cursor='pointer'
                                    fontWeight='medium'
                                    _hover={{ textDecoration: 'underline' }}
                                    onClick={() =>
                                      history.push(
                                        `${match.url.replace(/\/(products|suppliers).*/, '')}/suppliers/${v.supplierId}`
                                      )
                                    }>
                                    {v.supplierName ?? '—'}
                                  </Text>
                                ) : (
                                  <Text fontWeight='medium'>{v.supplierName ?? '—'}</Text>
                                )}
                              </Td>
                              <Td>{v.brand ?? '—'}</Td>
                              <Td>{v.defaultUnit ?? '—'}</Td>
                              <Td isNumeric fontWeight='semibold'>
                                {v.price.toFixed(2)}
                              </Td>
                              <Td>
                                <Text fontSize='xs' color='gray.500' maxW='200px' isTruncated>
                                  {v.specs ?? '—'}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {/* Similar Products */}
              {profile?.similarProducts && profile.similarProducts.length > 0 && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Similar Products
                    </Text>
                    <CompetitorSuppliersSection similarProducts={profile.similarProducts} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {/* Price Benchmarking */}
              {profile?.priceBenchmark && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Price Benchmarking
                    </Text>
                    <PriceBenchmarkSection benchmark={profile.priceBenchmark} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {/* Demand Metrics */}
              {profile?.demand && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Demand Metrics
                    </Text>
                    <DemandMetricsSection metrics={profile.demand} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {/* Product Category */}
              {categoryTree && categoryTree.length > 0 && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Product Category
                    </Text>
                    <CategoryTreeSection categories={categoryTree} onCategoryClick={handleCategoryClick} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

            </>
          )}
        </DrawerBody>
      </DrawerContent>

      <CategoryIntelligenceDrawer
        isOpen={isCategoryOpen}
        onClose={onCategoryClose}
        categoryPath={selectedCategoryPath}
        categoryId={selectedCategoryId}
        depth={selectedCategoryDepth}
      />
    </Drawer>
  );
};
